import { useEffect, useMemo } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import {
    GetStandaloneMeasuringPointsDocument,
    type GetStandaloneMeasuringPointsQuery,
    type GetStandaloneMeasuringPointsQueryVariables,
} from '@/graphql/types';
import type { UseOrderAndPaginationControlReturn } from '../../../table/hooks';
import { createAfterCursor } from '../../../../utils/relay';
import type { MeasuringPointWithProject } from '../types';
import { mapOverviewQueryMeasuringPoints } from '../utils/measuringPoints';

interface UseStandaloneMeasuringPointsQueryArgs {
    archived: boolean;
    search: string;
    orderAndPaginationControl: UseOrderAndPaginationControlReturn;
    enabled: boolean;
}

export function useStandaloneMeasuringPointsQuery({
    archived,
    search,
    orderAndPaginationControl,
    enabled,
}: UseStandaloneMeasuringPointsQueryArgs) {
    const { pageSize, currentPage, orderBy, setTotalCount } = orderAndPaginationControl;

    const queryVariables = useMemo(
        () => ({
            archived,
            search: search.trim() || undefined,
            orderBy: orderBy || undefined,
            first: pageSize,
            after: createAfterCursor(currentPage, pageSize),
        }),
        [archived, search, orderBy, pageSize, currentPage]
    );

    const { data, loading, error, networkStatus } = useQuery<
        GetStandaloneMeasuringPointsQuery,
        GetStandaloneMeasuringPointsQueryVariables
    >(GetStandaloneMeasuringPointsDocument, {
        variables: queryVariables,
        skip: !enabled,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (!enabled || loading) {
            return;
        }

        const totalCount = data?.standaloneMeasuringPoints?.totalCount;
        setTotalCount(typeof totalCount === 'number' ? totalCount : 0);
    }, [data?.standaloneMeasuringPoints?.totalCount, enabled, loading, setTotalCount]);

    const measuringPoints = useMemo<MeasuringPointWithProject[]>(
        () =>
            mapOverviewQueryMeasuringPoints(data?.standaloneMeasuringPoints?.nodes, {
                projectId: null,
                projectName: null,
            }),
        [data?.standaloneMeasuringPoints?.nodes]
    );

    return {
        loading: loading && networkStatus === NetworkStatus.loading,
        updating: loading && networkStatus === NetworkStatus.setVariables,
        error,
        measuringPoints,
    };
}
